import { gql } from '@apollo/client';
import IconError from '@apollo/icons/default/IconError.svg';
import IconSuccess from '@apollo/icons/default/IconSuccess.svg';
import IconUnavailable from '@apollo/icons/default/IconUnavailable.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import { Spinner } from '@apollo/orbit';
import classnames from 'classnames';
import React from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import {
  DownstreamWorkflowStatusCounts,
  DownstreamWorkflowStatusCountsFragment,
} from '../checkWorkflowsListPage/checkWorkflowItem/DownstreamWorkflowStatusCounts';
import {
  GetTaskDisplayStatusTaskFragment,
  taskHasWarnings,
} from '../checkWorkflowsListPage/checkWorkflowItem/getTaskDisplayStatus';

export const TaskStatusIconTaskFragment = gql`
  fragment TaskStatusIconTaskFragment on CheckWorkflowTask {
    id
    ...GetTaskDisplayStatusTaskFragment
    ...DownstreamWorkflowStatusCountsFragment
  }
  ${GetTaskDisplayStatusTaskFragment}
  ${DownstreamWorkflowStatusCountsFragment}
`;

export const TaskStatusIcon = ({
  className,
  allowMultipleStatuses = false,
  task,
}: {
  className?: string;
  allowMultipleStatuses?: boolean;
  task:
    | GraphQLTypes.CheckWorkflowTaskStatus.PASSED
    | GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED
    | GraphQLTypes.TaskStatusIconTaskFragment;
}) => {
  const shouldDescriptionsWarn = useLDFlag(
    'gov-proposal-checks-descriptions-warn',
  );

  if (
    allowMultipleStatuses &&
    typeof task !== 'string' &&
    task.__typename === 'DownstreamCheckTask' &&
    task.results?.length
  ) {
    return (
      <DownstreamWorkflowStatusCounts
        downstreamTask={task}
        includeIcon={false}
      />
    );
  }
  const status = typeof task === 'string' ? task : task.status;
  switch (status) {
    case GraphQLTypes.CheckWorkflowTaskStatus.PASSED:
      return typeof task !== 'string' &&
        taskHasWarnings(task, shouldDescriptionsWarn) ? (
        <IconWarning className={classnames(className, 'text-icon-warning')} />
      ) : (
        <IconSuccess className={classnames(className, 'text-icon-success')} />
      );
    case GraphQLTypes.CheckWorkflowTaskStatus.BLOCKED:
      return (
        <IconUnavailable className={classnames(className, 'text-secondary')} />
      );
    case GraphQLTypes.CheckWorkflowTaskStatus.PENDING:
      return <Spinner className={className} size="xs" />;
    case GraphQLTypes.CheckWorkflowTaskStatus.FAILED:
      return <IconError className={classnames(className, 'text-icon-error')} />;
    default:
      assertUnreachable(status);
  }
};
